import { TrendingUp, TrendingDown, Minus } from "lucide-react";

/** Iter 178 · Brand-pulse sparkline + score badge.
 *  Pure SVG, no external chart lib. Renders a mini line chart of the last
 *  N pulse scores + a colour-coded band pill. */

const BAND_TONE = {
  accelerating: "text-emerald-700 bg-emerald-50 border-emerald-300",
  steady: "text-sky-700 bg-sky-50 border-sky-300",
  cooling: "text-amber-700 bg-amber-50 border-amber-300",
  stalled: "text-rose-700 bg-rose-50 border-rose-300",
};

const LINE_TONE = {
  accelerating: "#10b981",
  steady: "#0ea5e9",
  cooling: "#f59e0b",
  stalled: "#ef4444",
};

function bandOf(score) {
  if (score >= 75) return "accelerating";
  if (score >= 55) return "steady";
  if (score >= 35) return "cooling";
  return "stalled";
}

export default function BrandPulse({ score, trend, delta, compact = false }) {
  if (score === null || score === undefined) {
    return (
      <span className="text-[10px] text-muted-foreground italic"
              data-testid="brand-pulse-empty">
        No pulse
      </span>
    );
  }
  const band = bandOf(score);
  const line = LINE_TONE[band];
  const points = Array.isArray(trend) && trend.length > 1 ? trend : null;

  // Build SVG polyline path
  let poly = null;
  if (points) {
    const w = compact ? 48 : 72;
    const h = compact ? 16 : 22;
    const min = Math.min(...points);
    const max = Math.max(...points);
    const range = max - min || 1;
    const step = w / (points.length - 1);
    const coords = points.map((p, i) =>
      `${(i * step).toFixed(1)},${(h - ((p - min) / range) * h).toFixed(1)}`
    ).join(" ");
    poly = (
      <svg width={w} height={h} className="shrink-0" data-testid="brand-pulse-sparkline">
        <polyline points={coords} fill="none" stroke={line} strokeWidth="1.4"
                   strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    );
  }

  const dIcon = delta > 2 ? <TrendingUp className="w-3 h-3 text-emerald-600" />
    : delta < -2 ? <TrendingDown className="w-3 h-3 text-rose-600" />
    : <Minus className="w-3 h-3 text-muted-foreground" />;

  return (
    <span className="inline-flex items-center gap-1.5"
            data-testid="brand-pulse-chip" title={`Pulse ${score} · ${band}`}>
      <span className={`text-[10px] px-1.5 py-0.5 border font-mono font-semibold ${BAND_TONE[band] || ""}`}>
        {score}
      </span>
      {!compact && dIcon}
      {poly}
    </span>
  );
}
